// packages/kern/src/presets.ts — die drei Vorlagen (SPEC §3.11).
//
// KEIN DOM: außer `text-canvas.ts` darf keine Datei in `packages/kern/src`
// `document`, `window`, `navigator`, `fetch`, `localStorage` oder einen
// React-Import enthalten.
//
// Inhalte VERBATIM aus `docs/quellen/understand/prototype.json`:
// reusable_snippets[7] (`defaultPlan()` = PRESETS.lilasp.build()) und
// reusable_snippets[8] (PRESETS.isek, PRESETS.leer). Die Hilfsfunktion
// `B(lane,row,a,b,text,style,icon)` bleibt so, `base = parseYM("2026-10")`
// bzw. `parseYM("2027-01")`.
//
// Das erzeugte JSON muss die Goldens treffen:
// `/opt/lilasp-zeitplan/tests/golden/{standard,isek,leer}.plan.json` sind bis
// auf die `id`-Felder (die aus `uid()` kommen) gleich. Die vier Schlüssel aus
// A5 (`kunde`, `projektNr`, `schlagworte`, `vorlageName`) stehen hier ABSICHTLICH
// nicht: die Goldens kennen sie nicht, und der Server leitet sie aus `daten` ab.

import type { Eintrag, PiktogrammKey, Plan, StilKey } from "./types";
import { parseYM, uid } from "./zeit";

export interface Vorlagensatz { name: string; build(): Plan }

/** Die Balken-Kurzform des Prototyps: Monate relativ zu `base`. */
function bauer(base: number) {
  return (lane: string, row: number, a: number, b: number, text: string,
          style?: StilKey, icon?: PiktogrammKey): Eintrag => ({
    id: uid(), lane, row, a: base + a, b: base + b, text,
    style: style || "solid", icon: icon || "none",
    iconPos: "center", align: "center", color: "", size: 0, hoehe: 1, drehung: 0,
    iconColor: "", frei: false, dx: 0, dy: 0,
  });
}

function defaultPlan(): Plan {
  const base = parseYM("2026-10");
  const B = bauer(base);
  return {
    v: 1,
    title: "Grobzeitplan – Innenstadtkonzept",
    start: "2026-10", end: "2027-06", compressFrom: "2027-04", compressW: 1.25,
    bl: "RP", ferien: true, ferienMin: 3, ferienNames: false,
    format: "frei", margin: 8, fitRows: true, width: 1280, rowH: 44, fontScale: 1, grid: 0.5, guides: true, guidesExport: false,
    lanes: [
      { id: "l1", name: "Bausteine",   rows: 4, def: "solid" },
      { id: "l2", name: "Beteiligung", rows: 3, def: "solid" },
      { id: "l3", name: "Abstimmung",  rows: 2, def: "meilen" },
    ],
    items: [
      B("l1", 0, 0, 3, "Leistungsbaustein 1   Potentialanalyse und Bewertung", "outline"),
      B("l1", 0, 3, 6, "Leistungsbaustein 2   Maßnahmenkonzept mit Umsetzungsfahrplan", "outline"),
      B("l1", 0, 6, 8, "Abschluss", "outline"),
      B("l1", 1, 0, 1, "Grundlagenauswertung und Konzept-Screening"),
      B("l1", 1, 1, 2, "Gliederung in 12 bis 18 Strukturräume, GIS-Aufbau"),
      B("l1", 1, 2, 3, "Ermittlung und Bewertung der Potentialflächen"),
      B("l1", 1, 3, 4, "Handlungsfelder und Gestaltungsprinzipien"),
      B("l1", 1, 4, 5, "Priorisierung und Umsetzungsfahrplan"),
      B("l1", 1, 5, 6, "Endredaktion und Layout"),
      B("l1", 2, 0, 1, "Prüfung der Maßnahmensteckbriefe 1 bis 8"),
      B("l1", 2, 1, 3, "Bestandsaufnahme vor Ort, vier Erhebungsreisen"),
      B("l1", 2, 3, 5, "Maßnahmensteckbriefe mit Kostenschätzung für Umsetzung und Unterhaltung"),
      B("l1", 2, 5, 6, "Übergabe GIS-Daten und Einzelsteckbriefe"),
      B("l1", 3, 1, 2, "Klima-, Versiegelungs- und Verschattungskartierung"),
      B("l1", 3, 2, 3, "Potentialflächenkarte und Strukturraumsteckbriefe"),
      B("l1", 3, 3, 5, "Maßnahmenkarte, Prinzipschnitte, Visualisierungen"),
      B("l2", 0, 0, 1, "F1 Auftakt-Fachworkshop mit den Fachämtern", "solid", "megafon"),
      B("l2", 0, 2, 3, "F2 Potentialwerkstatt, Stadtspaziergänge, Jugendbeteiligung", "solid", "gruppe"),
      B("l2", 0, 3, 4, "Stakeholder-Forum mit Thementischen", "solid", "megafon"),
      B("l2", 0, 4, 5, "F3 Maßnahmenwerkstatt und Rückspiegelung", "solid", "megafon"),
      B("l2", 1, 1, 5, "Digitales Mapping und Onlinebefragung, Bodenteppich und Mitmach-Station im Projektbüro", "dashed"),
      B("l3", 0, 0, 1, "M1 Auftakt und Fachworkshop", "meilen"),
      B("l3", 0, 2, 3, "M2 Zwischenpräsentation und Gremienvorstellung", "meilen"),
      B("l3", 0, 4, 5, "M3 Maßnahmenkonzept abgestimmt", "meilen"),
      B("l3", 0, 5, 6, "M4 Abgabe des Konzepts", "meilen"),
      B("l3", 0, 6, 8, "M5 Abschlusspräsentation im Gremium", "meilen"),
      B("l3", 1, 0, 8, "Regelmäßig: zweiwöchiger digitaler Jour fixe mit dem Amt für Stadtentwicklung und Bauordnung\nKontinuierlich: GIS-basierte Bearbeitung, Beteiligungsdokumentation, Öffentlichkeitsarbeit", "outline"),
    ],
  };
}

export const PRESETS: { lilasp: Vorlagensatz; isek: Vorlagensatz; leer: Vorlagensatz } = {
  lilasp: {
    name: "LILASp Standard",
    build(): Plan { const p = defaultPlan(); return p; },
  },
  isek: {
    name: "ISEK / Konzept (3 Phasen)",
    build(): Plan {
      const base = parseYM("2027-01");
      const B = bauer(base);
      return {
        v: 1, title: "Grobzeitplan – ISEK-Fortschreibung", start: "2027-01", end: "2028-06", compressFrom: "", compressW: 1.25,
        bl: "NI", ferien: true, ferienMin: 3, ferienNames: false, format: "frei", margin: 8, fitRows: true, width: 1500, rowH: 44, fontScale: 1, grid: 0.5, guides: true, guidesExport: false,
        lanes: [{ id: "l1", name: "Bausteine", rows: 4, def: "solid" }, { id: "l2", name: "Beteiligung", rows: 2, def: "solid" }, { id: "l3", name: "Abstimmung", rows: 2, def: "meilen" }],
        items: [
          B("l1", 0, 0, 6, "Phase 1   Grundlagen und Analyse", "outline"),
          B("l1", 0, 6, 12, "Phase 2   Vertiefung und Synthese", "outline"),
          B("l1", 0, 12, 18, "Phase 3   Strategie und Maßnahmen", "outline"),
          B("l1", 1, 0, 2, "Datenkonzept (LB 1)"),
          B("l1", 1, 2, 6, "Daten- und Bestandsanalyse (LB 5)"),
          B("l1", 1, 6, 10, "Demografiestrategie (LB 7)"),
          B("l1", 1, 12, 16, "Leitbilder und Entwicklungsstrategie (LB 10)"),
          B("l1", 2, 1, 4, "Sozialstruktur- und Wohnungsmarktanalyse (LB 5/6)"),
          B("l1", 2, 8, 12, "Synthese (LB 9)"),
          B("l1", 2, 13, 17, "Handlungsfelder, Ziele und Maßnahmenkatalog (LB 11)"),
          B("l1", 3, 0, 18, "Projektmanagement, Qualitätssicherung, Dokumentation und Öffentlichkeitsarbeit", "dashed"),
          B("l2", 0, 2, 3, "Öffentliche Auftaktveranstaltung", "solid", "megafon"),
          B("l2", 0, 7, 10, "6 Themenworkshops", "solid", "gruppe"),
          B("l2", 0, 11, 12, "Öffentliche Zwischenpräsentation", "solid", "megafon"),
          B("l2", 0, 16, 17, "Öffentliche Abschlussveranstaltung", "solid", "megafon"),
          B("l3", 0, 0, 1, "Kick-off", "meilen"),
          B("l3", 0, 3, 4, "Lenkungsgruppe", "meilen"),
          B("l3", 0, 7, 8, "Lenkungsgruppe", "meilen"),
          B("l3", 0, 11, 12, "Lenkungsgruppe", "meilen"),
          B("l3", 0, 17, 18, "Politische Beschlussfassung", "meilen"),
          B("l3", 1, 0, 18, "Regelmäßig: Jour fixe je nach Bedarf · Kontinuierlich: Prozessbegleitung, digitales Projektboard, GIS-Datenbank", "outline"),
        ],
      };
    },
  },
  leer: {
    name: "Leeres Blatt",
    build(): Plan {
      return {
        v: 1, title: "Grobzeitplan", start: "2026-10", end: "2027-06", compressFrom: "", compressW: 1.25,
        bl: "NW", ferien: true, ferienMin: 3, ferienNames: false, format: "frei", margin: 8, fitRows: true, width: 1280, rowH: 44, fontScale: 1, grid: 0.5, guides: true, guidesExport: false,
        lanes: [{ id: "l1", name: "Bausteine", rows: 3, def: "solid" }, { id: "l2", name: "Beteiligung", rows: 2, def: "solid" }, { id: "l3", name: "Abstimmung", rows: 2, def: "meilen" }],
        items: [],
      };
    },
  },
};

export const PRESET_ORDER: Array<keyof typeof PRESETS> = ["lilasp", "isek", "leer"];
